/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * TractionSection — stats, built-today list, and case study.
 */
import { useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, CheckCircle2, FlaskConical, ChevronRight } from "lucide-react";
import PitchSection from "../PitchSection";
import SectionLabel from "../SectionLabel";
import DetailModal from "../DetailModal";
import InfoTooltip from "../InfoTooltip";
import AiBotCaseStudyImg from "../../../assets/ai-bot-casestudy.jpg";

export default function TractionSection({ data }) {
  const [showCaseStudy, setShowCaseStudy] = useState(false);

  return (
    <PitchSection id="traction" dark>
      <SectionLabel>{data.label}</SectionLabel>
      <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-8 leading-tight">
        {data.title}
      </h2>

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
        {data.stats.map((s, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.15 }}
            className="bg-[#0F1826]/60 border border-[#4FA9A1]/20 rounded-2xl p-6 text-center"
          >
            <p className="text-3xl sm:text-4xl font-extrabold text-[#4FA9A1] mb-1">{s.value}</p>
            <p className="text-base text-gray-400">{s.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Built today checklist */}
      <div className="mb-8">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-[#4FA9A1]" />
          Built & Running Today
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {data.builtToday.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="flex items-start gap-2 text-base text-gray-400"
            >
              <CheckCircle2 className="w-4 h-4 text-[#4FA9A1]/60 mt-0.5 flex-shrink-0" />
              {item}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Case study CTA */}
      <motion.button
        onClick={() => setShowCaseStudy(true)}
        whileHover={{ scale: 1.02 }}
        className="flex items-center gap-3 bg-gradient-to-r from-[#4FA9A1]/10 to-[#3B82F6]/10 border border-[#4FA9A1]/25 rounded-2xl p-5 w-full sm:w-auto text-left hover:border-[#4FA9A1]/40 transition-all"
      >
        <FlaskConical className="w-6 h-6 text-[#4FA9A1]" />
        <div className="flex-1">
          <p className="text-white font-semibold text-base">{data.caseStudy.title}</p>
          <p className="text-gray-400 text-sm">Click to view full case study details</p>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400" />
      </motion.button>

      {/* Case study modal */}
      <DetailModal
        open={showCaseStudy}
        onClose={() => setShowCaseStudy(false)}
        title={data.caseStudy.title}
      >
        <div className="space-y-6">
          <p className="text-gray-300 leading-relaxed">{data.caseStudy.body}</p>
          {data.caseStudy.image && (
            <div className="flex justify-center m-4 sm:m-[30px]">
              <img
                src={AiBotCaseStudyImg}
                alt={data.caseStudy.title}
                className="rounded-xl border border-white/10 max-w-full sm:max-w-[85%]"
              />
            </div>
          )}
          {data.caseStudy.note && (
            <div className="bg-[#3B82F6]/10 border border-[#3B82F6]/20 rounded-xl p-4">
              <p className="text-sm text-[#60A5FA] font-medium mb-1">Note</p>
              <p className="text-gray-300 text-base leading-relaxed">{data.caseStudy.note}</p>
            </div>
          )}
          {data.caseStudy.tooltip && (
            <p className="text-gray-400 text-xs italic">{data.caseStudy.tooltip}</p>
          )}
        </div>
      </DetailModal>
    </PitchSection>
  );
}
